import { mouseHover } from "components/website/partners/maps/hover";
import { withRoutePreviewLang } from "components/website/pages/articleTripHref";
import { useContext, useEffect, useRef } from "react";
import { flagUrl, GuideIcon, GuideLangContext } from "../discoverSections";
import { bestTimeIn, pointOfStop, stopsLine, type DurationGroup, type MapPoint, type MapRoute, type RouteImage } from "./data";
import { dateLocale, useRouteMapUi, withUnit } from "./copy";
import { Plus } from "./icons";
import { markerLook } from "./mapModel";
import { MarkerIcon } from "components/website/partners/shared/markers";
import styles from "./CountryRouteMap.module.scss";

/**
 * The Routes tab, as Ilse's v29 has it.
 *
 * First view: the trip-length chips (no counts), "Pick a route", and every ready-made route as a compact card (its photo, nights, best time,
 * stops), then "Or start from scratch". A card previews its route on the map while the mouse is over it, and the map takes them in turn
 * until the visitor first does something (CountryRouteMap). Every card carries a real link to its /routepreview page, which is only for
 * crawlers and screen readers: the card itself opens the route on the map instead of leaving.
 *
 * Opened route: its card (nights, best time, name, why pick it, a strip of the stops' photos), then a row per stop. "All routes" above it,
 * and Customise / Save this route at the panel's foot, are the shell's.
 */

export const GROUP_LABEL: Record<DurationGroup, string> = { "1week": "1 week", "10days": "10 days", "2weeks": "2 weeks", "3weeks": "3 weeks+" };

export const nightsLabel = (t: ReturnType<typeof useRouteMapUi>, nights: number): string => withUnit(t, nights, "night", "nights");

interface RoutesTabProps {
	/** the routes the open chip lets through */
	routes: MapRoute[];
	groups: DurationGroup[];
	filter: DurationGroup | "all";
	onFilter: (filter: DurationGroup | "all") => void;
	/** the chip's label: the KB's, else the dictionary's */
	groupLabel: (group: DurationGroup | "all") => string;
	points: MapPoint[];
	/** the photo that stands for each route */
	images: Map<string, RouteImage>;
	countryCode: string;
	/** how many places "start from scratch" offers */
	placeCount: number;
	/** the open route, or null for the first view */
	selected: MapRoute | null;
	/** the route the map shows in the first view: highlighted in the list */
	preview: string | null;
	onSelect: (route: MapRoute) => void;
	/** a mouse is over a card (null: it left): its route is on the map */
	onPreview: (id: string | null) => void;
	onScratch: () => void;
	/** the destination whose card is open on the map */
	placeSlug: string | null;
	/** a stop was chosen: its destination's card opens on the map */
	onPlace: (slug: string) => void;
	/** a mouse is over a stop (null: it left): the destination's pin lights up on the map */
	onHover: (slug: string | null) => void;
}

const RoutesTab = ({
	routes,
	groups,
	filter,
	onFilter,
	groupLabel,
	points,
	images,
	countryCode,
	placeCount,
	selected,
	preview,
	onSelect,
	onPreview,
	onScratch,
	placeSlug,
	onPlace,
	onHover,
}: RoutesTabProps) => {
	const t = useRouteMapUi();
	const lang = useContext(GuideLangContext);
	const locale = dateLocale(lang);
	const top = useRef<HTMLDivElement>(null);

	// The first view keeps its scroll; an opened route starts at its top.
	useEffect(() => {
		if (selected) top.current?.scrollIntoView?.({ block: "nearest" });
	}, [selected]);

	const flag = (
		<span className={styles.rmFlag}>
			<img src={flagUrl(countryCode)} alt="" />
		</span>
	);
	const best = (route: MapRoute) => bestTimeIn(route.bestTime, locale);

	if (selected) {
		const route = selected;
		return (
			<div ref={top} className={styles.opened}>
				<div className={styles.rcard} data-selected="true">
					<div className={styles.rtop}>
						<span className={styles.meta}>
							<span className={styles.pill}>
								{flag}
								<span className={styles.pillText}>{route.duration ?? nightsLabel(t, route.nights)}</span>
							</span>
							{route.bestTime && (
								<span className={styles.best}>
									<GuideIcon name="calendar" fill="currentColor" size={15} />
									<span>
										{t("bestTime", "Best time:")} <strong>{best(route)}</strong>
									</span>
								</span>
							)}
						</span>
						<h3 className={styles.rname}>{route.title}</h3>
						{(route.pitch || route.desc) && <p className={styles.why}>{route.pitch || route.desc}</p>}
					</div>
					{route.stops.length > 0 && (
						<ul className={styles.strip} aria-label={t("stopsAria", "Stops")}>
							{route.stops.map((stop, i) => {
								const point = pointOfStop(stop, points);
								const photo = point?.photo || stop.photo;
								const content = (
									<>
										<span className={styles.stopPhoto}>
											{photo ? <img src={photo} alt="" loading="lazy" /> : <GuideIcon name="marker" fill="#8aabb5" size={22} />}
											<i className={styles.stopNo}>{i + 1}</i>
										</span>
										<span className={styles.stopName}>{stop.name}</span>
										<span className={styles.stopNights}>{nightsLabel(t, stop.nights)}</span>
									</>
								);
								return (
									<li key={`${stop.name}-${i}`}>
										{point ? (
											<button
												type="button"
												className={`${styles.stop} ${placeSlug === point.slug ? styles.stopOn : ""}`}
												onClick={() => onPlace(point.slug)}
												{...mouseHover(onHover, point.slug)}
											>
												{content}
											</button>
										) : (
											// a stop that is no destination of the guide (Isan): nothing to open
											<span className={styles.stop}>{content}</span>
										)}
									</li>
								);
							})}
						</ul>
					)}
				</div>
				{route.stops.length > 0 && (
					<ol className={styles.days}>
						{route.stops.map((stop, i) => {
							const point = pointOfStop(stop, points);
							const photo = point?.photo || stop.photo;
							const look = markerLook(point?.type);
							const body = (
								<>
									<span className={styles.dayThumb} style={{ backgroundColor: look.tint }}>
										{photo ? <img src={photo} alt="" loading="lazy" /> : <MarkerIcon name={look.icon} size={22} />}
										<i className={styles.dayNo}>{i + 1}</i>
									</span>
									<span className={styles.dayName}>
										<b>{stop.name}</b>
										{point?.blurb && <span>{point.blurb}</span>}
									</span>
									<span className={styles.dayNights}>{nightsLabel(t, stop.nights)}</span>
								</>
							);
							return (
								<li key={`${stop.name}-${i}`}>
									{point ? (
										<button
											type="button"
											className={`${styles.day} ${placeSlug === point.slug ? styles.dayOn : ""}`}
											onClick={() => onPlace(point.slug)}
											{...mouseHover(onHover, point.slug)}
										>
											{body}
										</button>
									) : (
										<div className={styles.day}>{body}</div>
									)}
								</li>
							);
						})}
					</ol>
				)}
			</div>
		);
	}

	return (
		<div ref={top}>
			<div className={styles.filters} role="group" aria-label={t("filterLabel", "Trip length")}>
				{(["all", ...groups] as const).map((g) => (
					<button
						key={g}
						type="button"
						className={`${styles.rmChip} ${filter === g ? styles.chipOn : ""}`}
						aria-pressed={filter === g}
						onClick={() => onFilter(g)}
					>
						{groupLabel(g)}
					</button>
				))}
			</div>
			<div className={styles.pickHead} data-pick-head>
				<b>{t("stepPick", "Pick a route")}</b>
				<span>{t("pickSub", "Then make it your own")}</span>
			</div>
			<ul className={styles.mini}>
				{routes.map((route) => {
					const photo = images.get(route.id);
					const look = markerLook(photo?.type);
					const previewUrl = withRoutePreviewLang(`/routepreview/${route.id}`, lang);
					return (
						<li key={route.id} className={`${styles.miniCard} ${preview === route.id ? styles.miniOn : ""}`} data-route={route.id}>
							<button
								type="button"
								className={styles.miniBtn}
								onClick={() => onSelect(route)}
								onPointerEnter={(e) => e.pointerType === "mouse" && onPreview(route.id)}
								onPointerLeave={(e) => e.pointerType === "mouse" && onPreview(null)}
								onFocus={() => onPreview(route.id)}
								onBlur={() => onPreview(null)}
							>
								<span className={styles.miniImg} style={{ backgroundColor: look.tint }}>
									{photo?.photo ? <img src={photo.photo} alt="" loading="lazy" /> : <MarkerIcon name={look.icon} size={26} />}
								</span>
								<span className={styles.miniBody}>
									<span className={styles.meta}>
										<span className={styles.pill}>
											{flag}
											<span className={styles.pillText}>{route.duration ?? nightsLabel(t, route.nights)}</span>
										</span>
										{route.bestTime && (
											<span className={styles.bestMini}>
												<GuideIcon name="calendar" fill="currentColor" size={13} />
												{best(route)}
											</span>
										)}
									</span>
									<b className={styles.miniTitle}>{route.title}</b>
									<span className={styles.miniStops}>{stopsLine(route, " • ")}</span>
								</span>
							</button>
							{/* for crawlers and screen readers: the route's own page */}
							<a className={styles.srOnly} href={previewUrl}>
								{route.title}
							</a>
						</li>
					);
				})}
			</ul>
			<button type="button" className={styles.own} onClick={onScratch}>
				<span className={styles.ownPlus}>
					<Plus size={16} color="#fff" weight={2.8} />
				</span>
				<span>
					<b>{t("ownTitle", "Or start from scratch")}</b>
					<small>{t("ownSub", "Pick from {n} places in {country}", { n: placeCount })}</small>
				</span>
			</button>
		</div>
	);
};

export default RoutesTab;
